import React, { useState } from 'react';
import '../../admin/shell/AdminShell.css';
import './Settings.css';
import { useAuth } from '../../context/AuthContext';
import RevolutPayButton from '../../components/RevolutPayButton';
import useOwnerSettingsData from './useSettingsData';
import SalesReport from '../reports/SalesReport';
import ProductMix from '../reports/ProductMix';
import { sendBankTransferOrder, startJccCheckout, startPayPalCheckout } from '../../checkout/subscriptionPayment';
import {
  getSubscriptionPlans,
  formatSubscriptionNet,
} from '../../constants/subscriptionPlans';


const COLLAPSED_KEY = 'owAdminSidebarCollapsed';

const Icon = ({ children }) => (
  <svg className="admin-icon" viewBox="0 0 24 24" width="20" height="20" fill="none"
    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);

const NAV_ITEMS = [
  { key: 'profile', label: 'User profile', icon: <Icon><circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 3.6-6.5 8-6.5s8 2.5 8 6.5" /></Icon> },
  { key: 'restaurants', label: 'Restaurants', icon: <Icon><path d="M4 10l1.5-5h13L20 10M4 10v10h16V10M4 10h16M9 20v-5h6v5" /></Icon> },
  { key: 'sales', label: 'Sales reports', icon: <Icon><path d="M4 20V4M4 20h16M8 16v-4M12 16V8M16 16v-6" /></Icon> },
  { key: 'products', label: 'Product mix', icon: <Icon><circle cx="12" cy="12" r="9" /><path d="M14.5 9.5c-.6-.8-1.5-1.2-2.5-1.2-1.4 0-2.5.8-2.5 1.9 0 2.5 5 1.3 5 3.800 0 1.1-1.1 1.9-2.5 1.9-1.1 0-2-.4-2.6-1.2M12 6.500v1.800M12 15.700v1.800" /></Icon> },
];

const OwOwnerSettings = () => {
  const { authenticatedFetch, user, loading, error, restaurants, currencies, paymentMethods } = useOwnerSettingsData();
  const { email, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSED_KEY) === 'true');
  const [activeTab, setActiveTab] = useState('restaurants');
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [payMsg, setPayMsg] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null); // 'base' or 'standard'
  const [agentCode, setAgentCode] = useState('');
  const [paypalLoading, setPaypalLoading] = useState(null);
  const [jccLoading, setJccLoading] = useState(false);

  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
    setSelectedRestaurant(null);
  };

  const handlePaymentSubmit = async () => {
    if (!user || !selectedRestaurant || !selectedPlan || selectedPlan === 'standard') return;

    setIsSubmitting(true);
    try {
      await sendBankTransferOrder({
        user,
        restaurant: selectedRestaurant,
        planKey: selectedPlan,
        currencies,
        agentCode,
        authenticatedFetch,
      });
      setShowModal(false);
      setSelectedPlan(null);
      setAgentCode('');
      setPayMsg(false);
      alert('Payment details have been sent to your email!');
    } catch (err) {
      alert(`Failed to send payment details: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePayPalPayment = async (planKey) => {
    if (!selectedRestaurant || paypalLoading) return;

    setPaypalLoading(planKey);
    try {
      await startPayPalCheckout({
        restaurant: selectedRestaurant,
        email: user?.email,
        planKey,
        currencies,
        authenticatedFetch,
      });
    } catch (err) {
      alert(err.message || 'Failed to open PayPal.');
      setPaypalLoading(null);
    }
  };

  const handleJccPayment = async (planKey) => {
    if (!selectedRestaurant || jccLoading) return;

    setJccLoading(true);
    try {
      await startJccCheckout({
        restaurant: selectedRestaurant,
        planKey,
        currencies,
        authenticatedFetch,
      });
    } catch (err) {
      alert(err.message || 'Failed to open card payment.');
      setJccLoading(false);
    }
  };

  const subscription = getSubscriptionPlans(selectedRestaurant?.currency, currencies);

  return (
    <div className={`admin-shell${collapsed ? ' is-collapsed' : ''}`}>
      <aside className="admin-sidebar">
        <div className="admin-sidebar-top">
          <span className="admin-brand">OW Owner</span>
          <button
            type="button"
            className="admin-icon-btn"
            onClick={() => setCollapsed((prev) => {
              localStorage.setItem(COLLAPSED_KEY, String(!prev));
              return !prev;
            })}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!collapsed}
          >
            <Icon>
              <rect x="3" y="4" width="18" height="16" rx="2" />
              <path d="M9 4v16" />
              {collapsed ? <path d="m14 10 2 2-2 2" /> : <path d="m16 10-2 2 2 2" />}
            </Icon>
          </button>
        </div>

        <nav className="admin-nav" aria-label="Owner navigation">
          {NAV_ITEMS.map(({ key, label, icon }) => (
            <button
              key={key}
              type="button"
              className={`admin-nav-link${activeTab === key ? ' active' : ''}`}
              onClick={() => handleTabSwitch(key)}
              title={collapsed ? label : undefined}
            >
              {icon}
              <span className="admin-nav-label">{label}</span>
            </button>
          ))}
        </nav>

        <div className="admin-sidebar-bottom">
          {email && <span className="admin-user ph-no-capture" title={email}>{email}</span>}
          <button type="button" className="admin-nav-link" onClick={logout}
            title={collapsed ? 'Log out' : undefined}>
            <Icon><path d="M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3M10 8l-4 4 4 4M6 12h10" /></Icon>
            <span className="admin-nav-label">Log out</span>
          </button>
        </div>
      </aside>

      <section className="admin-content">
        <div className="settings-tab-content">
          {!selectedRestaurant && (
            <h1 className="settings-page-title">{NAV_ITEMS.find((i) => i.key === activeTab).label}</h1>
          )}

            {/* User Profile */}
            {activeTab === 'profile' && (
              <>
                {loading && <p className="settings-loading">Loading...</p>}
                {error && <div className="settings-error">⚠️ {error}</div>}
                {user && (
                  <div className="profile-fields">
                    {[['Email', user.email], ['Username', user.username], ['Phone', user.phone]].map(([label, value]) => (
                      <div key={label} className="profile-field">
                        <span className="profile-field-label">{label}</span>
                        <span className="profile-field-value">{value}</span>
                      </div>
                    ))}
                    <p className="profile-note">You can edit your details only in the owner mobile application.</p>
                  </div>
                )}
              </>
            )}

            {activeTab === 'sales' && user && (
              <SalesReport
                restaurants={restaurants}
                userId={user.id}
                authenticatedFetch={authenticatedFetch}
                currencies={currencies}
                paymentMethods={paymentMethods}
              />
            )}

            {activeTab === 'products' && user && (
              <ProductMix
                restaurants={restaurants}
                userId={user.id}
                authenticatedFetch={authenticatedFetch}
                currencies={currencies}
              />
            )}

            {/* Restaurants — list */}
            {activeTab === 'restaurants' && !selectedRestaurant && (
              <>
                {loading && <p className="settings-loading">Loading...</p>}
                {error && <div className="settings-error">⚠️ {error}</div>}
                {!loading && !error && restaurants.length === 0 && (
                  <p className="settings-loading">No restaurants found.</p>
                )}
                {!loading && restaurants.length > 0 && (
                  <div className="restaurant-list">
                    <div className="restaurant-list-header">
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="#4CAF50">
                        <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
                      </svg>
                      <span className="restaurant-list-header-title">Subscription</span>
                      <span className="restaurant-list-header-sub">Select a restaurant to manage subscription plan</span>
                    </div>
                    {restaurants.map((r) => (
                      <button
                        key={r.id}
                        className="restaurant-list-item"
                        onClick={() => setSelectedRestaurant(r)}
                      >
                        <span className="restaurant-list-name">{r.name}</span>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4CAF50" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="9 18 15 12 9 6"/>
                        </svg>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Restaurants — detail */}
            {activeTab === 'restaurants' && selectedRestaurant && (
              <div className="restaurant-detail-view">
                <button className="back-btn" onClick={() => setSelectedRestaurant(null)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4CAF50" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="15 18 9 12 15 6"/>
                  </svg>
                  Back
                </button>
                <div className="restaurant-detail-name">{selectedRestaurant.name}</div>

                {/* Subscription plans container */}
                <div className="plans-container">

                  {/* BASE BUSINESS PLAN */}
                  <div className="plan-card plan-card-base">
                    <div className="plan-card-top">
                      <div className="plan-badge">{selectedRestaurant.subscription === 3 ? 'Active' : 'Currently Available'}</div>
                      <div className="plan-name">Base Business Plan</div>

                      {/* Promotional Badge */}
                      <div className="plan-promo-badge">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M21.41 11.58l-9-9C12.05 2.22 11.55 2 11 2H4c-1.1 0-2 .9-2 2v7c0 .55.22 1.05.59 1.42l9 9c.36.36.86.58 1.41.58.55 0 1.05-.22 1.41-.59l7-7c.37-.36.59-.86.59-1.41 0-.55-.23-1.06-.59-1.42zM5.5 7C4.67 7 4 6.33 4 5.5S4.67 4 5.5 4 7 4.67 7 5.5 6.33 7 5.5 7z"/>
                        </svg>
                        First Month FREE
                      </div>

                      <div className="plan-price">
                        <div className="plan-price-original">
                          <span className="plan-currency-small">{subscription.symbol}</span>
                          <span className="plan-amount-crossed">{subscription.baseListPrice}</span>
                        </div>
                        <div className="plan-price-promo">
                          <span className="plan-currency">{subscription.symbol}</span>
                          <span className="plan-amount">{subscription.base.net}</span>
                          <span className="plan-period">net / month</span>
                        </div>
                      </div>
                      <div className="plan-savings">Save {subscription.symbol}{subscription.savingsAmount}/month • {subscription.savingsPercent}% OFF</div>
                    </div>

                    <div className="plan-divider" />

                    {/* Plan Features - Always shown */}
                    <div className="plan-features">
                      <div className="plan-feature-group">
                        <div className="plan-feature-group-label">Orders</div>
                        <div className="plan-feature">
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="#81C784"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/></svg>
                          Confirm, cancel & accept orders
                        </div>
                        <div className="plan-feature">
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="#81C784"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/></svg>
                          Payment completion tracking
                        </div>
                        <div className="plan-feature">
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="#81C784"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/></svg>
                          Discount management
                        </div>
                      </div>

                      <div className="plan-feature-group">
                        <div className="plan-feature-group-label">Payments</div>
                        <div className="plan-feature">
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="#81C784"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/></svg>
                          Manage payments
                        </div>
                        <div className="plan-feature plan-feature-with-note">
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="#81C784"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/></svg>
                          <span>
                            Online payments
                            <span className="plan-feature-note">(Your own payment provider)</span>
                          </span>
                        </div>
                      </div>

                      <div className="plan-feature-group">
                        <div className="plan-feature-group-label">Staff</div>
                        <div className="plan-feature">
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="#81C784"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/></svg>
                          Create, edit & delete staff
                        </div>
                        <div className="plan-feature">
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="#81C784"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/></svg>
                          Role-based permissions
                        </div>
                      </div>

                      <div className="plan-feature-group">
                        <div className="plan-feature-group-label">Management</div>
                        <div className="plan-feature">
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="#81C784"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/></svg>
                          Restaurant details & settings
                        </div>
                        <div className="plan-feature">
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="#81C784"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/></svg>
                          Company details & settings
                        </div>
                        <div className="plan-feature">
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="#81C784"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/></svg>
                          Sales reports
                        </div>
                        <div className="plan-feature">
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="#81C784"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/></svg>
                          Product mix
                        </div>
                      </div>
                    </div>

                    {/* Bottom section wrapper for consistent height */}
                    <div className="plan-bottom-section">
                      {/* Active Subscription Status - Only when subscription is active */}
                      {selectedRestaurant.subscription === 3 && (
                        <div className="subscription-active-message subscription-active-message-base">
                          <div className="subscription-active-icon">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#81C784" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                              <polyline points="22 4 12 14.01 9 11.01"/>
                            </svg>
                          </div>
                          <h3 className="subscription-active-title">Your Base Plan is Active</h3>
                          <p className="subscription-active-text">
                            Your subscription is currently active and all features above are available for this restaurant.
                          </p>
                          <div className="subscription-active-status subscription-active-status-base">
                            <span className="subscription-active-status-label">Status:</span>
                            <span className="subscription-active-status-value">Active</span>
                          </div>
                        </div>
                      )}

                      {/* Payment Option - Only when subscription is not active */}
                      {selectedRestaurant.subscription !== 3 && (
                        <>
                          {/* Limited Time Offer Urgency */}
                          <div className="plan-urgency-message">
                            <div className="plan-urgency-icon">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10"/>
                                <polyline points="12 6 12 12 16 14"/>
                              </svg>
                            </div>
                            <div className="plan-urgency-content">
                              <div className="plan-urgency-title">⚡ Limited Time Offer</div>
                              <div className="plan-urgency-text">First month free ends soon • Act now to secure this deal</div>
                            </div>
                          </div>

                          <div className="plan-pay-actions ph-no-capture">
                            <button className="plan-pay-btn plan-pay-btn-base" onClick={() => { setSelectedPlan('base'); setShowModal(true); }}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/>
                              </svg>
                              Bank Transfer Payment
                            </button>
                            <button
                              type="button"
                              className="plan-pay-btn plan-pay-btn-paypal"
                              onClick={() => handlePayPalPayment('base')}
                              disabled={paypalLoading === 'base'}
                            >
                              <span className="paypal-label">
                                Pay with
                                <span className="paypal-word"><span className="paypal-pay">Pay</span><span className="paypal-pal">Pal</span></span>
                              </span>
                            </button>
                            {/* Only this account can see card payment while JCC is in sandbox. */}
                            {email?.toLowerCase() === 'test@test.com' && (
                              <button
                                type="button"
                                className="plan-pay-btn plan-pay-btn-jcc"
                                onClick={() => handleJccPayment('base')}
                                disabled={jccLoading}
                              >
                                <span className="jcc-label">
                                  Pay with
                                  <span className="jcc-word">JCC</span>
                                </span>
                              </button>
                            )}
                            <RevolutPayButton
                              planKey="base"
                              restaurant={selectedRestaurant}
                              currencies={currencies}
                              email={user?.email}
                              authenticatedFetch={authenticatedFetch}
                            />
                          </div>

                          {payMsg && (
                            <div className="pay-unavailable-msg">
                              <p>This service is currently unavailable.</p>
                              <p>See you soon 😊</p>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

        </div>
      </section>

      {/* Subscription Payment Modal */}
      {showModal && selectedRestaurant && selectedPlan && (
        <div className="subscription-modal-overlay ph-no-capture" onClick={(e) => {
          if (e.target.classList.contains('subscription-modal-overlay')) setShowModal(false);
        }}>
          <div className="subscription-modal">
            <div className="subscription-modal-header">
              <div className="subscription-modal-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                  <line x1="1" y1="10" x2="23" y2="10"/>
                </svg>
              </div>
              <h3 className="subscription-modal-title">{selectedPlan === 'base' ? 'Base Plan' : 'Standard Plan'} Payment Order</h3>
              <p className="subscription-modal-subtitle">Confirm your subscription</p>
            </div>
            <div className="subscription-modal-body">
              <p className="subscription-modal-message">
                You are about to order a <strong>{selectedPlan === 'base' ? 'Base Business Plan' : 'Standard Business Plan'}</strong> subscription for your restaurant.
                Payment details and instructions will be sent to your registered email address.
              </p>
              <div className="subscription-modal-info">
                <div className="subscription-modal-info-item">
                  <span className="subscription-modal-info-label">Plan</span>
                  <span className="subscription-modal-info-value">{selectedPlan === 'base' ? 'Base Business' : 'Standard Business'}</span>
                </div>
                <div className="subscription-modal-info-item">
                  <span className="subscription-modal-info-label">Amount</span>
                  <span className="subscription-modal-info-value">{formatSubscriptionNet(selectedPlan === 'base' ? subscription.base : subscription.standard, subscription.symbol)} NET</span>
                </div>
                {selectedPlan === 'base' && (
                  <div className="subscription-modal-info-item subscription-modal-promo">
                    <span className="subscription-modal-info-label">Promotion</span>
                    <span className="subscription-modal-info-value subscription-modal-promo-badge">🎁 First Month FREE</span>
                  </div>
                )}
                <div className="subscription-modal-info-item">
                  <span className="subscription-modal-info-label">Email</span>
                  <span className="subscription-modal-info-value">{user?.email}</span>
                </div>
              </div>
              <div className="subscription-modal-agent">
                <label className="subscription-modal-agent-label">Agent Referral Code (Optional)</label>
                <input
                  className="subscription-modal-agent-input"
                  type="text"
                  value={agentCode}
                  onChange={(e) => setAgentCode(e.target.value)}
                  placeholder="Add code here - only for first-time subscribers"
                  maxLength={100}
                  disabled={isSubmitting}
                />
              </div>
              <div className="subscription-modal-actions">
                <button
                  className="subscription-modal-btn subscription-modal-btn-cancel"
                  onClick={() => { setShowModal(false); setSelectedPlan(null); setAgentCode(''); }}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  className="subscription-modal-btn subscription-modal-btn-confirm"
                  onClick={handlePaymentSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="subscription-modal-loading"></span>
                      Processing...
                    </>
                  ) : (
                    'Confirm & Pay'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OwOwnerSettings;

